/* ============================================================================
   In-memory store for the mock adapter.
   Copies of the data modules so mutations (completing a task, redeeming an
   offer, sending a message) behave like a real backend for the session,
   without ever leaking into the seeded constants.
   ========================================================================== */

import type {
  ActivityEntry,
  CareTask,
  Campaign,
  ChatMessage,
  CommunityChannel,
  DiseaseReport,
  Lesson,
  Mission,
  Nursery,
  Plant,
  Product,
  Quiz,
  RewardAccount,
  RewardOffer,
  Species,
  User,
} from '@core/types'
import { CARE_TASKS, DISEASE_REPORTS, PLANTS } from './data/garden'
import { ACTIVITY, MISSIONS, REWARD_ACCOUNT, REWARD_OFFERS, USER } from './data/rewards'
import { SPECIES } from './data/species'
import {
  CAMPAIGNS,
  COMMUNITY_CHANNELS,
  COMMUNITY_MESSAGES,
  LESSONS,
  NURSERIES,
  PRODUCTS,
  QUIZZES,
} from './data/content'

interface MockDb {
  user: User
  species: Species[]
  plants: Plant[]
  careTasks: CareTask[]
  diseaseReports: DiseaseReport[]
  rewardAccount: RewardAccount
  offers: RewardOffer[]
  missions: Mission[]
  activity: ActivityEntry[]
  lessons: Lesson[]
  quizzes: Quiz[]
  nurseries: Nursery[]
  products: Product[]
  campaigns: Campaign[]
  channels: CommunityChannel[]
  messages: ChatMessage[]
}

function clone<T>(items: readonly T[]): T[] {
  return items.map((item) => ({ ...item }))
}

function seed(): MockDb {
  return {
    user: { ...USER, notificationPrefs: { ...USER.notificationPrefs } },
    species: clone(SPECIES),
    plants: clone(PLANTS),
    careTasks: clone(CARE_TASKS),
    diseaseReports: clone(DISEASE_REPORTS),
    rewardAccount: { ...REWARD_ACCOUNT, history: clone(REWARD_ACCOUNT.history) },
    offers: clone(REWARD_OFFERS),
    missions: clone(MISSIONS),
    activity: clone(ACTIVITY),
    lessons: clone(LESSONS),
    quizzes: clone(QUIZZES),
    nurseries: clone(NURSERIES),
    products: clone(PRODUCTS),
    campaigns: clone(CAMPAIGNS),
    channels: clone(COMMUNITY_CHANNELS),
    messages: clone(COMMUNITY_MESSAGES),
  }
}

export const db: MockDb = seed()

/** Restores the seeded data — used by the developer tools in Settings. */
export function resetDb(): void {
  Object.assign(db, seed())
}

let sequence = 0

/** Predictable, readable ids: `ct-2001`, `ms-5007`, ... */
export function nextId(prefix: string, floor: number): string {
  sequence += 1
  return `${prefix}-${floor + sequence}`
}
